/**
 * A container's day as one strip (20/10/2026): one segment per drop in
 * time order, coloured by what it is now — sent, sending, scheduled, held,
 * stopped, skipped, due, waiting — with a "now" mark before the first drop
 * still to come. Hover a segment: its time, list, amount and state.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC } from 'react';

import { cn } from '@/lib/utils';

import type { DayPlanDropRow } from '../../../drop-generator.types';
import { instantText } from '../../../utils/clock/dk-label';
import { useCountryClock } from '../../shared/time/country-clock-context';
import { dropState, type DropState, STATE_WORDS } from './drop-state';

export const STATE_CLASS: Record<DropState, string> = {
  sent: 'bg-success',
  sending: 'bg-success animate-pulse motion-reduce:animate-none',
  scheduled: 'bg-primary/70',
  staged: 'bg-primary/50',
  held: 'bg-warning',
  left: 'bg-destructive/60 ring-1 ring-destructive',
  shadow: 'bg-warning/50',
  stopped: 'bg-destructive',
  skipped: 'bg-muted-foreground/25',
  overdue: 'bg-warning ring-1 ring-warning',
  waiting: 'bg-primary/25',
  planned: 'bg-muted-foreground/20',
};

const sms = (n: number) => `${n.toLocaleString('en-US')} SMS`;

export const DropStrip: FC<{ drops: DayPlanDropRow[]; now: Date; className?: string }> = ({ drops, now, className }) => {
  const clock = useCountryClock();
  const sorted = [...drops].sort((a, b) => a.atUtc.localeCompare(b.atUtc));
  const firstToCome = sorted.findIndex((d) => new Date(d.atUtc) >= now);
  if (sorted.length === 0) return <span className={cn('text-[11px] text-muted-foreground', className)}>no drop today</span>;
  return (
    <span className={cn('flex h-3 min-w-0 items-center gap-px', className)} role="img" aria-label={`${sorted.length} drops today`} data-testid="drop-strip">
      {sorted.map((drop, i) => {
        const state = dropState(drop, now);
        const sentAt = drop.runtime?.sentAt ? ` · sent ${instantText(drop.runtime.sentAt, clock)}` : '';
        return (
          <span
            key={`${drop.slotId ?? 'x'}-${i}`}
            data-state={state}
            title={`${instantText(drop.atUtc, clock)} · ${drop.listName ?? 'no list'} · ${sms(drop.amount)} · ${STATE_WORDS[state]}${sentAt}`}
            className={cn('h-full min-w-[3px] flex-1 rounded-[1px]', STATE_CLASS[state], i === firstToCome && i > 0 && 'ml-1 border-l-2 border-foreground/70 pl-px')}
          />
        );
      })}
    </span>
  );
};
